import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Mail, Phone, Linkedin, ArrowRight, Loader2 } from "lucide-react";
import { NetworkNodes } from "./decor";
import { REVENUE_GOALS } from "../data";
import { Reveal, SectionLabel } from "./primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", revenue_goal: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-[#F4F6F0] px-4 py-3 text-sm text-[#23271F] placeholder:text-[#9aa48d] outline-none transition-colors focus:border-brand/60";

  return (
    <section id="contact" data-testid="contact-section" className="relative isolate overflow-hidden bg-white py-24 md:py-32">
      <NetworkNodes className="right-6 top-12 w-[34%] max-w-md opacity-60" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-brand/15 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-8">
        <Reveal>
          <div className="text-center">
            <SectionLabel>Let's Talk Revenue</SectionLabel>
            <h2 className="mx-auto mt-4 max-w-3xl font-serif text-4xl font-semibold tracking-tight text-[#23271F] sm:text-5xl lg:text-6xl">
              Book a discovery call.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[#586150]">
              One focused conversation to pressure-test your GTM and map the fastest path to
              enterprise revenue. No pitch — just a senior read on where the leverage is.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          {/* Form */}
          <Reveal>
            <div className="rounded-2xl border border-line bg-white shadow-[0_6px_28px_rgba(28,38,18,0.06)] p-8">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    data-testid="contact-success"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  >
                    <CheckCircle2 className="h-14 w-14 text-[#5C8A1E]" />
                    <h3 className="mt-6 font-serif text-2xl font-medium text-[#23271F]">Thank you — request received.</h3>
                    <p className="mt-3 max-w-sm text-sm text-[#586150]">
                      We'll be in touch within one business day. Prefer to lock a time now? Use the
                      scheduler on the right.
                    </p>
                    <button
                      data-testid="contact-reset"
                      onClick={() => { setForm({ name: "", company: "", email: "", revenue_goal: "", message: "" }); setStatus("idle"); }}
                      className="mt-8 text-sm font-semibold text-[#5C8A1E] hover:underline"
                    >
                      Submit another request
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-4"
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input required data-testid="input-name" className={inputCls} placeholder="Full name" value={form.name} onChange={update("name")} />
                      <input required data-testid="input-company" className={inputCls} placeholder="Company" value={form.company} onChange={update("company")} />
                    </div>
                    <input required type="email" data-testid="input-email" className={inputCls} placeholder="Work email" value={form.email} onChange={update("email")} />
                    <select required data-testid="input-revenue-goal" className={`${inputCls} ${form.revenue_goal ? "text-[#23271F]" : "!text-[#9aa48d]"}`} value={form.revenue_goal} onChange={update("revenue_goal")}>
                      <option value="" disabled>Revenue goal…</option>
                      {REVENUE_GOALS.map((g) => (
                        <option key={g} value={g} className="text-[#23271F]">{g}</option>
                      ))}
                    </select>
                    <textarea data-testid="input-message" rows={4} className={inputCls} placeholder="What's the revenue challenge you're solving? (optional)" value={form.message} onChange={update("message")} />

                    {status === "error" && (
                      <p data-testid="contact-error" className="text-sm text-red-400">Something went wrong. Please try again.</p>
                    )}

                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={status === "loading"}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-ink-950 transition-colors hover:bg-brand-hover disabled:opacity-60"
                    >
                      {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Book a Discovery Call <ArrowRight className="h-4 w-4" /></>}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>

          {/* Direct contact */}
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col gap-6">
              <div className="grid gap-3 rounded-2xl border border-line bg-white shadow-[0_6px_28px_rgba(28,38,18,0.06)] p-6">
                <a href="mailto:vicky.thorat@xsellsolutions.in" data-testid="contact-email" className="flex items-center gap-3 text-sm text-[#474e3f] transition-colors hover:text-[#5C8A1E]">
                  <Mail className="h-4 w-4 text-[#5C8A1E]" /> vicky.thorat@xsellsolutions.in
                </a>
                <a href="tel:+919960811371" data-testid="contact-phone" className="flex items-center gap-3 text-sm text-[#474e3f] transition-colors hover:text-[#5C8A1E]">
                  <Phone className="h-4 w-4 text-[#5C8A1E]" /> +91-9960 811 371
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="flex items-center gap-3 text-sm text-[#474e3f] transition-colors hover:text-[#5C8A1E]">
                  <Linkedin className="h-4 w-4 text-[#5C8A1E]" /> Connect on LinkedIn
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
